import React from "react";
import { Box, HStack, Icon, IconButton, InputGroup } from "@chakra-ui/react";
import classNames from "classnames";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import { IoCalendarOutline, IoCloseOutline } from "react-icons/io5";
import "react-datepicker/dist/react-datepicker.css";
import { generateId, useField } from "duck";
import { Types } from "../../duck";
import InputLayout from "../input-layout";
import { Header } from "./components";
import { filterTime } from "./duck";
import styles from "./DatePicker.module.css";

interface DatePickerProps<WithRange extends boolean | undefined = undefined>
  extends Omit<
      ReactDatePickerProps<any, WithRange>,
      | "name"
      | "onChange"
      | "onBlur"
      | "onCalendarClose"
      | "selected"
      | "value"
      | "isClearable"
    >,
    Types.BaseInputProps {
  onChange?(
    date: WithRange extends false | undefined
      ? Date | null
      : [Date | null, Date | null],
    event?: React.SyntheticEvent<any> | undefined
  ): void;
  onBlur?(): void;
  value?: Date | null;
  isClearable?: boolean;
  size?: "sm" | "md";
  className?: string;
  timeFilter?: "passed" | "future";
  futureYears?: number;
}

const DatePicker = <WithRange extends boolean | undefined = undefined>({
  name,
  label,
  labelHidden,
  inputLayoutProps,
  value,
  dateFormat = "MM/dd/yyyy",
  isClearable = true,
  onChange,
  onBlur,
  size = "md",
  className,
  timeFilter,
  futureYears,
  ...props
}: DatePickerProps<WithRange>): React.ReactElement => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const labelId = React.useMemo(() => name || generateId(), [name]);

  const fieldProps = useField(name);

  return (
    <InputLayout
      id={labelId}
      label={label}
      labelHidden={labelHidden}
      errorMessage={fieldProps.error}
      {...inputLayoutProps}
      input={
        <InputGroup
          className={classNames(styles.wrapper, className, {
            [styles.wrapperSm]: size === "sm"
          })}
          data-invalid={!!fieldProps.error}
          position="relative"
        >
          <ReactDatePicker
            data-invalid={!!fieldProps.error}
            showYearDropdown
            showMonthDropdown
            dateFormat={dateFormat}
            selected={value || fieldProps.value || null}
            name={labelId}
            onCalendarClose={() => {
              if (onBlur) {
                onBlur();
              } else if (fieldProps.onBlur) {
                fieldProps.onBlur();
              }
            }}
            onChange={date => {
              if (!isClearable && date === null) {
                return null;
              }

              if (onChange) {
                onChange((date || null) as any);
              } else if (fieldProps.onChange) {
                fieldProps.onChange(date || null);
              }
            }}
            filterTime={timeFilter ? filterTime(timeFilter) : undefined}
            ref={instance => {
              if (fieldProps.ref) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                fieldProps.ref(instance?.input || null);
              }
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              inputRef.current = instance?.input || null;
            }}
            renderCustomHeader={({
              date,
              changeYear,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled
            }) => (
              <Header
                date={date}
                changeYear={changeYear}
                futureYears={futureYears}
                decreaseMonth={decreaseMonth}
                increaseMonth={increaseMonth}
                prevMonthButtonDisabled={prevMonthButtonDisabled}
                nextMonthButtonDisabled={nextMonthButtonDisabled}
              />
            )}
            {...props}
          />
          <HStack
            position="absolute"
            right="0"
            spacing={0}
            top={size === "sm" ? "0px" : "4px"}
          >
            {isClearable && (fieldProps.value || value) && (
              <IconButton
                size={size === "md" ? "sm" : "xs"}
                display="flex"
                aria-label="Clear"
                color="gray.900"
                variant="ghost"
                mr="10px"
                _hover={{
                  background: "rgba(0, 0, 0, 0.06)"
                }}
                onClick={() => {
                  if (onChange) {
                    onChange(null as any);
                  } else if (fieldProps.onChange) {
                    fieldProps.onChange(null);
                  }

                  if (onBlur) {
                    onBlur();
                  } else if (fieldProps.onBlur) {
                    fieldProps.onBlur();
                  }
                }}
                icon={<Icon boxSize="6" as={IoCloseOutline} />}
              />
            )}
            <Box
              w="32px"
              h="32px"
              display="flex"
              alignItems="center"
              onClick={() => {
                inputRef.current?.focus();
              }}
              cursor="pointer"
            >
              <Icon
                as={IoCalendarOutline}
                bg="white"
                boxSize={size === "sm" ? "4" : "5"}
                color="gray.900"
              />
            </Box>
          </HStack>
        </InputGroup>
      }
    />
  );
};

export default DatePicker;
